import { Box, Paper } from "@mui/material";
import * as React from "react";

const MIN_WIDTH = 280;
const MAX_WIDTH = 400;
const STORAGE_KEY = "ramp-editor-panel-width";

type Props = {
  position: "left" | "right";
  visible: boolean;
  children: React.ReactNode;
  width?: number;
  header?: React.ReactNode;
  resizable?: boolean;
};

export const FloatingPanel: React.FC<Props> = ({
  position,
  visible,
  children,
  width: initialWidth = 300,
  header,
  resizable = false,
}) => {
  // Initialize width from localStorage or prop
  const [panelWidth, setPanelWidth] = React.useState<number>(() => {
    if (!resizable) return initialWidth;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
        return parsed;
      }
    }
    return initialWidth;
  });

  const [isResizing, setIsResizing] = React.useState(false);

  const handleResizeStart = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = panelWidth;

      const handleMouseMove = (e: MouseEvent) => {
        // For right panel: dragging left (negative delta) = wider
        // For left panel: dragging right (positive delta) = wider
        const delta = position === "right"
          ? startX - e.clientX
          : e.clientX - startX;
        const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
        setPanelWidth(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        // Save final width
        localStorage.setItem(STORAGE_KEY, String(panelWidth));
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [panelWidth, position]
  );

  // Save width when it changes (for real-time persistence)
  React.useEffect(() => {
    if (resizable && !isResizing) {
      localStorage.setItem(STORAGE_KEY, String(panelWidth));
    }
  }, [panelWidth, resizable, isResizing]);

  const effectiveWidth = resizable ? panelWidth : initialWidth;

  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        top: 16,
        bottom: 16,
        [position]: visible ? 16 : -(effectiveWidth + 32),
        width: effectiveWidth,
        display: "flex",
        flexDirection: "column",
        transition: isResizing ? "none" : "all 0.2s ease",
        borderRadius: 2,
        zIndex: 10,
        overflow: "hidden",
      }}
    >
      {/* Resize handle */}
      {resizable && (
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            position: "absolute",
            [position === "right" ? "left" : "right"]: 0,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: "ew-resize",
            zIndex: 20,
            transition: "background-color 0.15s ease",
            "&:hover, &:active": {
              backgroundColor: "primary.main",
            },
          }}
        />
      )}

      {header && (
        <Box
          sx={{
            flexShrink: 0,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {header}
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        {children}
      </Box>
    </Paper>
  );
};
